<script>
  import { createEventDispatcher, tick } from "svelte";
  import isEqual from "lodash/isEqual";
  import { DimText, LinearDisplayFormat } from "dimtext";

  import Calculator from "./Calculator.svelte";

  import dimSettings from "@local/extensions/utilities/dim-settings.js";
  import CalculatorIcon from "@local/assets/icons/calculator.svg";

  /** @type {string | null} */
  export let value;
  export let disabled = false;
  export let validator = (v) => true;
  export let coercer = (v) => v;
  export let settings;
  export let allowNull = false;
  export let max = undefined;
  export let min = undefined;

  const dispatch = createEventDispatcher();

  let input;
  let focused = false;
  let inputContents;
  let lastValue;
  let calculator;
  let showCalculator = false;

  $: ds = dimSettings(settings);
  $: dt = new DimText({ defaultUnit: ds.displayUnit });
  $: inputValue = value === "Mixed" ? value : value?.toString();
  $: displayValue = value === "Mixed" ? value : formatDisplayValue(value, ds);
  $: update(value, ds.dimFormat);
  $: valid = validate(inputContents);

  function formatDisplayValue(value, ds) {
    try {
      const v = value?.format(LinearDisplayFormat[ds.dimFormat], ds.dimPrecision, {
        displayUnit: ds.displayUnit,
        showUnit: true,
        unitSymbols: "ascii",
      });
      if (v) return v;
    } catch (e) {
      return null;
    }
    return null;
  }

  function update(value, sdf) {
    if (isEqual(value, lastValue)) return;
    lastValue = value;
    inputContents = focused ? inputValue : displayValue;
  }

  function validate(inputContents) {
    const result = inputContents && inputContents !== "Mixed" && dt.parse(inputContents);

    if (!result || !result.ok) return false;

    try {
      const dim = result.value;
      if (!validator(dim)) return false;
      if (min) {
        const mincompare = min.compare(dim);
        if (mincompare > 0) return false;
      }

      if (max) {
        const maxcompare = max.compare(dim);
        if (maxcompare < 0) return false;
      }

      return true;
    } catch (err) {
      return false;
    }
  }

  function parseInput(val) {
    try {
      if (val === "" && allowNull && value !== null) {
        value = null;
        dispatch("input", { value: null });
        return;
      }

      let result = dt.parse(val);
      if (!result.ok) throw result.err;
      if (value?.toString() !== result.value.toString()) {
        const coerced = coercer(result.value);
        if (valid) {
          value = coerced;
          dispatch("input", { value: coerced });
        }
      }
    } catch (err) {
      dispatch("error", err);
    }
  }

  function focus() {
    dispatch("focus");
    input.focus();
  }

  async function handleFocus(evt) {
    focused = true;
    inputContents = inputValue;
    await tick();
    input.select();
  }

  function handleKeydown(evt) {
    if (evt.key === "Enter") {
      evt.stopPropagation();
      input.blur();
    } else if (evt.key === "Escape") {
      input.blur();
    }
  }

  async function handleBlur(evt) {
    focused = false;
    parseInput(evt.target.value);
    await tick();
    inputContents = displayValue;
    dispatch("blur");
  }

  function insertCalculatorValue(e) {
    const { value: result } = e.detail;
    const resultString = result.toString();

    if (value?.toString() !== resultString) {
      const coerced = coercer(result);
      if (validator(resultString)) {
        value = coerced;
        dispatch("input", { value: coerced });
      }
    }

    toggleCalculator();
  }

  async function toggleCalculator() {
    showCalculator = !showCalculator;
    await tick();

    if (showCalculator && calculator) {
      calculator.activate();
    }
  }
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<div class="flex items-center container" class:focused class:showCalculator>
  <input
    bind:this={input}
    class="input"
    {disabled}
    bind:value={inputContents}
    on:focus={handleFocus}
    on:keydown={handleKeydown}
    on:blur={handleBlur}
    on:focus={handleFocus} />
  {#if !disabled}
    <button class="calculator-button" tabindex="-1" on:click|stopPropagation={toggleCalculator}>
      <CalculatorIcon />
    </button>
  {/if}
  {#if showCalculator}
    <Calculator
      bind:this={calculator}
      settings={ds}
      on:cancel={toggleCalculator}
      on:input={insertCalculatorValue} />
  {/if}
</div>

<style lang="scss">
  input.input {
    @apply p-0.5 border border-transparent rounded;
    background-color: none;
    outline: none;
    min-width: 0;
    max-width: 100%;

    &:hover,
    &:focus {
      @apply border-gray-300;
    }
  }

  .input {
    @apply min-w-0 grow shrink bg-transparent ml-2;

    &:hover {
      @apply border;
    }
  }

  .container {
    .calculator-button {
      @apply hidden right-0;
    }

    &:hover .calculator-button,
    &.focused .calculator-button,
    &.showCalculator .calculator-button {
      @apply block;
    }
  }
</style>
